import React, { useEffect, useState } from 'react'

import {
  closestCorners,
  defaultDropAnimation,
  DndContext,
  DragOverlay,
  DropAnimation,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { useTranslation } from 'react-i18next'
import { DataItem } from 'scrivito'

import { KanbanColumn } from './KanbanColumn'
import { KanbanBody } from './KanbanColumnBody'
import { KanbanHeader } from './KanbanColumnHeader'
import { KanbanItem } from './KanbanColumnItem'
import { KanbanContainer } from './KanbanContainer'
import { KeyboardSensor, PointerSensor } from './Sensors'
import { BoardSections } from './types'
import {
  getStatusTitle,
  getItemById,
  initializeBoard,
  getSectionPrice,
  setStatus,
  getSectionProbability,
} from './util'
import { errorToast, showToast } from '../Toast'
import { DataItemDrawer } from '@/Atomic/Widgets/DataItemDrawer'
import { ToastMessageType } from '@/Components/Toast/const'
import { useDragEvents } from '@/hooks/useDragEvents'
import { useDataItemDrawer } from '@/utils/CRUD/hooks/useDataItemDrawer'
import { reportError } from '@/utils/reportError'

interface KanbanViewProps {
  dataItems: DataItem[]
  schema: DataItem
  dataClassName: string
}

export const KanbanView: React.FC<KanbanViewProps> = ({
  dataItems,
  schema,
  dataClassName,
}) => {
  const { t } = useTranslation()
  const [boardSections, setBoardSections] = useState<BoardSections>({})

  useEffect(() => {
    const animationFrame = window.requestAnimationFrame(() => {
      const boardSections = initializeBoard(dataItems, schema)
      setBoardSections(boardSections)
    })

    return () => window.cancelAnimationFrame(animationFrame)
  }, [dataItems, schema])

  const [activeItemId, setActiveItemId] = useState<null | string>(null)
  const { drawerItem, setDrawerItem, isDrawerOpen, openDrawer, closeDrawer } =
    useDataItemDrawer()

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const onDrop = (item: DataItem, status: string, allowed: boolean) => {
    if (allowed) {
      setStatus(item, status).catch((error) => {
        errorToast(error)
        reportError('Kanban set state Error', error)
      })
    } else {
      showToast(ToastMessageType.ERROR, t('errors.kanban.stateNotAllowed'))
    }
  }

  const { handleDragStart, handleDragOver, handleDragEnd } = useDragEvents(
    setActiveItemId,
    boardSections,
    setBoardSections,
    dataItems,
    schema,
    onDrop,
  )

  const dropAnimation: DropAnimation = {
    ...defaultDropAnimation,
  }

  const item = activeItemId ? getItemById(dataItems, activeItemId) : null

  const onOpenItemDrawer = (item: DataItem) => {
    setDrawerItem(item)
    openDrawer()
  }

  if (dataItems.length === 0) {
    return null
  }

  return (
    <KanbanContainer>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        {Object.keys(boardSections).map((key) => {
          const probability = getSectionProbability(key, schema)
          return (
            <KanbanColumn key={key}>
              <KanbanHeader
                title={getStatusTitle(key, schema)}
                probability={probability}
                price={getSectionPrice(
                  boardSections[key] || [],
                  schema,
                  probability,
                )}
                count={boardSections[key]?.length || 0}
                dataClassName={dataClassName}
              />
              <KanbanBody
                id={key}
                items={boardSections[key] || []}
                schema={schema}
                onOpenItemDrawer={onOpenItemDrawer}
              />
            </KanbanColumn>
          )
        })}
        <DragOverlay dropAnimation={dropAnimation}>
          {item ? (
            <KanbanItem
              item={item}
              schema={schema}
              preview
              onOpenItemDrawer={onOpenItemDrawer}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
      {drawerItem && (
        <DataItemDrawer
          key={drawerItem.id()}
          isOpen={isDrawerOpen}
          close={closeDrawer}
          dataItem={drawerItem}
        />
      )}
    </KanbanContainer>
  )
}
